import { useEffect, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { jod } from "@astroplanet/shared";
import { useToast } from "../../context/ToastContext";
import { keys, useDiscountRedemptions, useDiscountRules } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { formatDateTime, localName, money } from "../../lib/format";
import type { DiscountRule } from "../../lib/types";
import { Empty, Pill, SectionHeader } from "../ui/Badges";

/** Percent rules are edited as "10" (%), fixed rules as "1.000" (JOD). */
const toInput = (r: Pick<DiscountRule, "kind" | "value">) => (r.kind === "percent" ? String(r.value / 100) : money(r.value));
const fromInput = (kind: "percent" | "fixed", s: string) => (kind === "percent" ? Math.round(Number(s) * 100) : jod(Number(s)));

function RuleRow({ rule }: { rule: DiscountRule }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const [kind, setKind] = useState(rule.kind);
  const [value, setValue] = useState(toInput(rule));
  const [approval, setApproval] = useState(rule.requiresApproval);
  const [note, setNote] = useState(rule.requiresNote);
  const [active, setActive] = useState(rule.active);
  const [names, setNames] = useState({ name: rule.name, nameAr: rule.nameAr ?? "" });
  const [applies, setApplies] = useState<"pass" | "total">(rule.appliesTo);
  const [valid, setValid] = useState({ from: (rule.validFrom ?? "").slice(0, 10), to: (rule.validTo ?? "").slice(0, 10) });
  const [p, setP] = useState<Record<string, string>>({
    visitNumber: String(rule.params.visitNumber ?? 2),
    withinDays: rule.params.withinDays == null ? "" : String(rule.params.withinDays),
    minSiblingsToday: String(rule.params.minSiblingsToday ?? 1),
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setKind(rule.kind); setValue(toInput(rule)); setApproval(rule.requiresApproval); setNote(rule.requiresNote); setActive(rule.active);
    setNames({ name: rule.name, nameAr: rule.nameAr ?? "" }); setApplies(rule.appliesTo); setValid({ from: (rule.validFrom ?? "").slice(0, 10), to: (rule.validTo ?? "").slice(0, 10) });
  }, [rule]);

  const amount = fromInput(kind, value);
  const okAmount = Number.isFinite(amount) && amount > 0 && (kind === "fixed" || amount <= 10_000);

  async function save() {
    setBusy(true);
    try {
      const params: Record<string, unknown> = { applies_to: applies };
      if (rule.condition === "second_visit") Object.assign(params, { visitNumber: Number(p.visitNumber) || 2, withinDays: (p.withinDays ?? "").trim() === "" ? null : Number(p.withinDays) });
      if (rule.condition === "siblings") Object.assign(params, { minSiblingsToday: Number(p.minSiblingsToday) || 1 });
      await api.put(`/discounts/rules/${rule.id}`, { name: names.name.trim(), nameAr: names.nameAr.trim() || null, kind, value: amount, requiresApproval: approval, requiresNote: note, active, params, validFrom: valid.from || null, validTo: valid.to ? `${valid.to}T23:59:59` : null });
      await qc.invalidateQueries({ queryKey: keys.discountRules });
      toast(t("discounts.saved"), "success");
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`rounded-2xl border p-3 ${active ? "border-gray-200 bg-gray-50" : "border-gray-200 bg-gray-100 opacity-70"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-black text-cosmic-navy">{localName(rule, locale)} <span className="num font-mono text-[10px] text-gray-400">{rule.code}</span></div>
          <div className="text-[11px] text-gray-500">{t(`coupon.cond.${rule.condition}`)}</div>
        </div>
        <label className="flex items-center gap-1.5 text-xs font-bold">
          <input type="checkbox" role="switch" className="rounded text-cosmic-purple focus:ring-0" checked={active} onChange={(e) => setActive(e.target.checked)} />
          {t("discounts.active")}
        </label>
      </div>
      <div className="mt-2 grid grid-cols-2 items-end gap-2 sm:grid-cols-4">
        <div><label className="label" htmlFor={`nm-${rule.id}`}>{t("discounts.nameEn")}</label><input id={`nm-${rule.id}`} className="field" value={names.name} onChange={(e) => setNames({ ...names, name: e.target.value })} /></div>
        <div><label className="label" htmlFor={`na-${rule.id}`}>{t("discounts.nameAr")}</label><input id={`na-${rule.id}`} className="field" dir="rtl" value={names.nameAr} onChange={(e) => setNames({ ...names, nameAr: e.target.value })} /></div>
        <div><label className="label" htmlFor={`vf-${rule.id}`}>{t("discounts.validFrom")}</label><input id={`vf-${rule.id}`} className="field num" type="date" value={valid.from} onChange={(e) => setValid({ ...valid, from: e.target.value })} /></div>
        <div><label className="label" htmlFor={`vt-${rule.id}`}>{t("discounts.validTo")}</label><input id={`vt-${rule.id}`} className="field num" type="date" value={valid.to} onChange={(e) => setValid({ ...valid, to: e.target.value })} /></div>
      </div>
      <div className="mt-2 grid grid-cols-2 items-end gap-2 sm:grid-cols-6">
        <div><label className="label" htmlFor={`ap-${rule.id}`}>{t("discounts.appliesTo")}</label><select id={`ap-${rule.id}`} className="field" value={applies} onChange={(e) => setApplies(e.target.value as "pass" | "total")}><option value="pass">{t("discounts.onPass")}</option><option value="total">{t("discounts.onTotal")}</option></select></div>
        <div>
          <label className="label" htmlFor={`k-${rule.id}`}>{t("discounts.kind")}</label>
          <select id={`k-${rule.id}`} className="field" value={kind} onChange={(e) => { const k = e.target.value as "percent" | "fixed"; setKind(k); setValue(k === "percent" ? "10" : "1.000"); }}>
            <option value="percent">{t("discounts.percent")}</option>
            <option value="fixed">{t("discounts.fixed")}</option>
          </select>
        </div>
        <div>
          <label className="label" htmlFor={`v-${rule.id}`}>{kind === "percent" ? t("discounts.valuePercent") : t("discounts.valueJod")}</label>
          <input id={`v-${rule.id}`} className="field num font-mono" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
        {rule.condition === "second_visit" && (
          <>
            <div><label className="label" htmlFor={`vn-${rule.id}`}>{t("discounts.visitNumber")}</label><input id={`vn-${rule.id}`} className="field num font-mono" type="number" min={2} value={p.visitNumber} onChange={(e) => setP({ ...p, visitNumber: e.target.value })} /></div>
            <div><label className="label" htmlFor={`wd-${rule.id}`}>{t("discounts.withinDays")}</label><input id={`wd-${rule.id}`} className="field num font-mono" type="number" min={1} placeholder="∞" value={p.withinDays} onChange={(e) => setP({ ...p, withinDays: e.target.value })} /></div>
          </>
        )}
        {rule.condition === "siblings" && (
          <div className="col-span-2"><label className="label" htmlFor={`ms-${rule.id}`}>{t("discounts.minSiblings")}</label><input id={`ms-${rule.id}`} className="field num font-mono" type="number" min={1} value={p.minSiblingsToday} onChange={(e) => setP({ ...p, minSiblingsToday: e.target.value })} /></div>
        )}
        <label className="flex items-center gap-1.5 text-[11px] font-bold"><input type="checkbox" className="rounded text-cosmic-purple focus:ring-0" checked={approval} onChange={(e) => setApproval(e.target.checked)} />{t("discounts.requiresApproval")}</label>
        <label className="flex items-center gap-1.5 text-[11px] font-bold"><input type="checkbox" className="rounded text-cosmic-purple focus:ring-0" checked={note} onChange={(e) => setNote(e.target.checked)} />{t("discounts.requiresNote")}</label>
        <button type="button" className="btn-dark" disabled={busy || !okAmount || names.name.trim() === "" || (!!valid.from && !!valid.to && valid.from > valid.to)} onClick={save}>{t("discounts.save")}</button>
      </div>
    </div>
  );
}

function AddCustom() {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ code: "", name: "", nameAr: "", kind: "percent" as "percent" | "fixed", value: "10" });

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await api.post("/discounts/rules", { code: f.code.trim(), name: f.name.trim(), nameAr: f.nameAr.trim() || undefined, kind: f.kind, value: fromInput(f.kind, f.value) });
      await qc.invalidateQueries({ queryKey: keys.discountRules });
      toast(t("discounts.saved"), "success");
      setOpen(false);
      setF({ code: "", name: "", nameAr: "", kind: "percent", value: "10" });
    } catch (err) {
      toastError(err);
    }
  }
  if (!open) return <button type="button" className="btn-ghost" onClick={() => setOpen(true)}>＋ {t("discounts.addCustom")}</button>;
  return (
    <form onSubmit={submit} className="grid grid-cols-2 items-end gap-2 rounded-2xl border border-dashed border-gray-300 p-3 sm:grid-cols-6">
      <div><label className="label" htmlFor="nc-code">{t("discounts.code")}</label><input id="nc-code" className="field num font-mono" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} pattern="[A-Za-z0-9_]{2,30}" required /></div>
      <div><label className="label" htmlFor="nc-name">{t("discounts.nameEn")}</label><input id="nc-name" className="field" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></div>
      <div><label className="label" htmlFor="nc-ar">{t("discounts.nameAr")}</label><input id="nc-ar" className="field" value={f.nameAr} onChange={(e) => setF({ ...f, nameAr: e.target.value })} /></div>
      <div>
        <label className="label" htmlFor="nc-kind">{t("discounts.kind")}</label>
        <select id="nc-kind" className="field" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as "percent" | "fixed", value: e.target.value === "percent" ? "10" : "1.000" })}>
          <option value="percent">{t("discounts.percent")}</option><option value="fixed">{t("discounts.fixed")}</option>
        </select>
      </div>
      <div><label className="label" htmlFor="nc-val">{f.kind === "percent" ? t("discounts.valuePercent") : t("discounts.valueJod")}</label><input id="nc-val" className="field num font-mono" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} /></div>
      <div className="flex gap-1"><button type="submit" className="btn-primary flex-1">{t("discounts.create")}</button><button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</button></div>
    </form>
  );
}

/** Managers: edit the coupon rules, add custom ones, and see who got what discount. */
export function DiscountsPanel() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const rules = useDiscountRules(true);
  const redemptions = useDiscountRedemptions();

  return (
    <section className="card space-y-3 p-4 sm:p-5" aria-label={t("discounts.title")}>
      <SectionHeader icon="🎟️" title={t("discounts.title")} subtitle={t("discounts.subtitle")}
        right={redemptions.data && <Pill tone="amber">{t("discounts.today", { count: redemptions.data.today.count })}: <span className="num">{money(redemptions.data.today.total)}</span> {t("currency")}</Pill>} />
      <p className="rounded-xl bg-amber-50 p-2.5 text-[11px] font-bold text-amber-900">ℹ️ {t("discounts.placeholderNote")}</p>
      <div className="space-y-2.5">{rules.data?.map((r) => <RuleRow key={r.id} rule={r} />)}</div>
      <AddCustom />
      <div>
        <h3 className="mb-1.5 text-xs font-black uppercase tracking-wide text-gray-500">{t("discounts.redemptions")}</h3>
        {!redemptions.data?.redemptions.length ? <Empty>{t("discounts.noRedemptions")}</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500">
                <tr><th className="px-2 py-2 text-start">{t("discounts.when")}</th><th className="px-2 py-2 text-start">{t("discounts.rule")}</th><th className="px-2 py-2 text-start">{t("table.explorer")}</th><th className="px-2 py-2 text-end">{t("discounts.amount")}</th><th className="px-2 py-2 text-start">{t("discounts.note")}</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
                {redemptions.data.redemptions.slice(0, 15).map((r) => (
                  <tr key={r.id}>
                    <td className="num px-2 py-2 font-mono text-[11px] text-gray-500">{formatDateTime(r.createdAt, locale)}</td>
                    <td className="px-2 py-2">{localName({ name: r.name, nameAr: r.nameAr }, locale)}</td>
                    <td className="px-2 py-2">{r.kidName}<div className="text-[10px] font-normal text-gray-400">{r.guardianName}</div></td>
                    <td className="num px-2 py-2 text-end font-mono text-emerald-700">-{money(r.amount)}</td>
                    <td className="px-2 py-2 text-[11px] font-normal text-gray-500">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
